import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";
import type {
  CaseView,
  EntscheiderWahl,
  InstantiatorGrantView,
  ProcessInstanceView,
  ProcessPluginRefView,
} from "@/types";

function useClient() {
  const { session, logout } = useAuth();
  return () => createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
}

// #300: Instanz-Lifecycle. Objekt-Urteile (Inhaber/PDP) fällt der Master —
// fremde Instanzen antworten 404 generisch, die UI kennt kein zweites Regelwerk.

export interface ConfigureInstancePatch {
  agentId?: string | null;
  trigger?: { kind: string; config: Record<string, unknown> } | null;
  inputDefaults?: Record<string, string | number | boolean>;
  settingsOverrides?: Record<string, string | number | boolean>;
  model?: string | null;
  /**
   * #320 (UC-2): Liste = voller Ersatz der Prozess-Vorgabe · `null` = wieder erben ·
   * weggelassen = unangetastet. Drei verschiedene Aussagen — die Karte schickt deshalb
   * NIE `plugins: []` als Ersatz für „nichts geändert".
   */
  plugins?: ProcessPluginRefView[] | null;
  /**
   * #330 (UC-1): die Entscheider-Wahl je Freigabe-Schritt.
   *
   * ⚠⚠ **VOLLER ERSATZ** — wie `settingsOverrides`, NICHT je Schritt gemischt: was hier nicht
   * steht, ist danach weg (`{}` = keine eigene Wahl mehr). Wer nur den gerade geänderten
   * Schritt schickt, löscht die Wahl aller übrigen. `baueEntscheiderPatch` baut deshalb den
   * vollständigen Stand: gespeicherte Sammlung als Basis, darüber die berührten Schritte.
   *
   * ⚠ Weggelassen heisst „unverändert" — und genau so bleibt es, wenn in dieser Sitzung
   * nichts angefasst wurde oder das Ergebnis dasselbe sagt wie der gespeicherte Stand
   * (netto-null erzeugt keinen Diff).
   *
   * ⚠ Beschnitten auf die freigegebenen Schalter werden nur die BERÜHRTEN Schritte; ein
   * unberührter Bestands-Eintrag geht verbatim mit. Ihn hier zu beschneiden hiesse, beim
   * Speichern von Cron oder Modell nebenbei eine fremde Entscheidung zu löschen — das
   * Räumen von Drift gehört dem Server-Sweep, der es protokolliert.
   */
  entscheiderOverrides?: Record<string, EntscheiderWahl>;
  /** #P002 (UC-3): VOLLER Ersatz der Modell-Wahl je Schritt (`{}` = keine eigene Wahl). */
  stepModelOverrides?: Record<string, string>;
}

function invalidate(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: queryKeys.resource("process-instances") });
}

export function useInstantiateProcess(): UseMutationResult<ProcessInstanceView, Error, { templateId: string }> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: (input) => getClient().post<ProcessInstanceView>("/process-instances", input),
    onSuccess: () => invalidate(qc),
  });
}

export function useConfigureInstance(): UseMutationResult<
  ProcessInstanceView,
  Error,
  { id: string; patch: ConfigureInstancePatch }
> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: ({ id, patch }) => getClient().patch<ProcessInstanceView>(`/process-instances/${id}`, patch),
    onSuccess: () => invalidate(qc),
  });
}

export function usePauseInstance(): UseMutationResult<ProcessInstanceView, Error, string> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: (id) => getClient().post<ProcessInstanceView>(`/process-instances/${id}/pause`, {}),
    onSuccess: () => invalidate(qc),
  });
}

export function useResumeInstance(): UseMutationResult<ProcessInstanceView, Error, string> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: (id) => getClient().post<ProcessInstanceView>(`/process-instances/${id}/resume`, {}),
    onSuccess: () => invalidate(qc),
  });
}

export function useDeleteInstance(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: (id) => getClient().delete<void>(`/process-instances/${id}`),
    onSuccess: () => invalidate(qc),
  });
}

export interface ProvisionResult {
  instance: ProcessInstanceView;
  provisioned: string[];
  approvalRequired: string[];
  failed: string[];
  /**
   * #320 (UC-4): dieselbe Runde deckt die Plugins. ADDITIV und optional — gegen einen
   * älteren Master fehlt das Feld, und dann darf die Oberfläche kein Ergebnis erfinden.
   *
   * ⚠ Review L2: `enabled` = in DIESER Runde aktiviert, `already` = war schon zugewiesen.
   * Die Meldung darf nur das Erste als Erfolg zeigen — sonst behauptet sie eine
   * Aktivierung, die nie stattgefunden hat.
   */
  plugins?: Array<ProcessPluginRefView & { result: "enabled" | "already" | "approvalRequired" | "failed" }>;
}

export function useProvisionConnectors(): UseMutationResult<ProvisionResult, Error, string> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: (id) => getClient().post<ProvisionResult>(`/process-instances/${id}/provision-connectors`, {}),
    onSuccess: () => invalidate(qc),
  });
}

/**
 * #301 (UC-3) / #303 (UC-2): einen Lauf von Hand starten.
 *
 * Nur bei Auslöser „manuell" und nur an einer bereiten Einrichtung — beides urteilt der
 * Master, die Oberfläche zeigt den Knopf bloss dort, wo er Sinn ergibt. `input` trägt die
 * Pflichtfelder des Start-Kontrakts; leer weglassen (der Master füllt aus den
 * Vorbelegungen).
 *
 * Invalidiert BEIDE Ebenen: der neue Lauf gehört in die Lauf-Liste, und die Einrichtung
 * zeigt ihre Läufe daneben.
 */
export function useStartCase(): UseMutationResult<
  CaseView,
  Error,
  { id: string; input?: Record<string, string | number | boolean>; dryRun?: boolean; model?: string }
> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: ({ id, input, dryRun, model }) =>
      getClient().post<CaseView>(`/process-instances/${id}/start`, {
        ...(input && Object.keys(input).length > 0 ? { input } : {}),
        ...(dryRun ? { dryRun: true } : {}),
        // #P001 (UC-4): das Modell reist NUR mit, wenn es gewählt wurde — ohne Feld gilt die
        // Kette Instanz → Agent-Default → Plattform-Default (ein leerer String wäre ein 400).
        ...(model ? { model } : {}),
      }),
    onSuccess: () => {
      invalidate(qc);
      void qc.invalidateQueries({ queryKey: queryKeys.resource("cases") });
    },
  });
}

export function useSetInstantiators(): UseMutationResult<
  InstantiatorGrantView[],
  Error,
  { processId: string; userIds: string[] }
> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: ({ processId, userIds }) =>
      getClient().put<InstantiatorGrantView[]>(`/processes/${processId}/instantiators`, { userIds }),
    onSuccess: (_r, { processId }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.subResource("processes", processId, "instantiators") });
    },
  });
}
